import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '@deloitte-us-consulting/gp-sdk';
import {
  loadRegistry,
  onRegistryRetry,
  peekRegistry,
  retryRegistry,
  uiApps,
  type PluginEntry,
  type RegistryLoad,
} from './registry';

export interface RegisteredApps {
  apps: PluginEntry[];
  /** True until the first registry read settles (either way). Lets the App Store
   *  launcher show skeleton tiles instead of flashing "no apps" on every load.
   *  False from the start when an earlier read already succeeded this session. */
  loading: boolean;
  /** True when the settled read failed (the platform API threw and there was no
   *  local fallback), so `apps` being empty says nothing about the user's access.
   *  The launcher shows an error instead of "no apps". */
  error: boolean;
  /** Re-reads the registry after a failure. Every mounted consumer (the side nav
   *  as well as the launcher) goes back to loading and then settles on the new
   *  result, so one Retry recovers the whole shell without a page reload. */
  retry: () => void;
}

type RegisteredAppsState = Omit<RegisteredApps, 'retry'>;

function initialState(): RegisteredAppsState {
  const seeded = peekRegistry();
  return seeded
    ? { apps: uiApps(seeded), loading: false, error: false }
    : { apps: [], loading: true, error: false };
}

/**
 * The registered, enabled apps that contribute a UI — the shell's own answer to
 * "what can this user actually open".
 *
 * Shared by the sidebar and the App Store launcher deliberately: one source here
 * means the two surfaces cannot drift into disagreeing about which apps exist.
 *
 * `loadRegistry` memoizes a successful load, so mounting this in several places
 * costs one fetch, and returning to the launcher starts from the cached list. A
 * failed load yields an empty list with `error: true` rather than throwing — a
 * registry outage should degrade the nav, not blank the shell. It is retried by
 * the next mount, or in place by `retry()`, which every mounted instance hears.
 */
export function useRegisteredApps(): RegisteredApps {
  const { accessToken } = useAuth();
  const [state, setState] = useState<RegisteredAppsState>(initialState);

  useEffect(() => {
    let alive = true;
    const settle = (load: Promise<RegistryLoad>) =>
      load.then(
        ({ entries, failed }) => {
          if (alive) setState({ apps: uiApps(entries), loading: false, error: failed });
        },
        () => {
          if (alive) setState({ apps: [], loading: false, error: true });
        },
      );
    settle(loadRegistry(() => accessToken));
    const unsubscribe = onRegistryRetry((load) => {
      // Only a failed instance shows the loading state again; one that already
      // has apps keeps them on screen while the shared result comes in.
      setState((current) => (current.error ? { ...current, loading: true, error: false } : current));
      settle(load);
    });
    return () => {
      alive = false;
      unsubscribe();
    };
  }, [accessToken]);

  const retry = useCallback(() => retryRegistry(() => accessToken), [accessToken]);

  return useMemo(() => ({ ...state, retry }), [state, retry]);
}
